import React, { useState, useEffect } from 'react';
import { Client, Account, ID, Databases } from 'appwrite';
import './index.css';

// Configuration Appwrite
const client = new Client()
  .setEndpoint('https://fra.cloud.appwrite.io/v1')
  .setProject('693d1a3c00113a792edf');

const account = new Account(client);
const databases = new Databases(client);

const DATABASE_ID = '693d1a3c00113a792edf';
const COLLECTION_ID = 'users_profiles';

function App() {
  const [currentPage, setCurrentPage] = useState('login');
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    gender: '',
    acceptCGU: false
  });

  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const currentUser = await account.get();
      setUser(currentUser);
      await loadUserProfile(currentUser.$id);
      setCurrentPage('account');
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const loadUserProfile = async (userId) => {
    try {
      const response = await databases.getDocument(
        DATABASE_ID,
        COLLECTION_ID,
        userId
      );
      setUserProfile(response);
    } catch (error) {
      console.log('Profil non trouvé:', error);
    }
  };

  const handleSignup = async () => {
    setError('');
    setSuccess('');

    if (signupData.password !== signupData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (signupData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (!signupData.acceptCGU) {
      setError('Vous devez accepter les CGU');
      return;
    }

    if (!signupData.firstName || !signupData.lastName || !signupData.birthDate || !signupData.gender || !signupData.email) {
      setError('Tous les champs sont requis');
      return;
    }

    try {
      const newUser = await account.create(
        ID.unique(),
        signupData.email,
        signupData.password,
        `${signupData.firstName} ${signupData.lastName}`
      );

      try {
        await databases.createDocument(
          DATABASE_ID,
          COLLECTION_ID,
          newUser.$id,
          {
            userId: newUser.$id,
            firstName: signupData.firstName,
            lastName: signupData.lastName,
            birthDate: signupData.birthDate,
            gender: signupData.gender,
            email: signupData.email,
            createdAt: new Date().toISOString()
          }
        );
      } catch (dbError) {
        console.log('Erreur création profil:', dbError);
      }

      await account.createEmailPasswordSession(signupData.email, signupData.password);
      
      setSuccess('Compte créé avec succès !');
      setTimeout(() => {
        checkUser();
      }, 1500);
    } catch (error) {
      setError(error.message || 'Erreur lors de la création du compte');
    }
  };

  const handleLogin = async () => {
    setError('');

    if (!loginData.email || !loginData.password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    try {
      await account.createEmailPasswordSession(loginData.email, loginData.password);
      setSuccess('Connexion réussie !');
      setTimeout(() => {
        checkUser();
      }, 1000);
    } catch (error) {
      setError('Email ou mot de passe incorrect');
    }
  };

  const handleLogout = async () => {
    try {
      await account.deleteSession('current');
      setUser(null);
      setUserProfile(null);
      setCurrentPage('login');
    } catch (error) {
      console.error('Erreur déconnexion:', error);
    }
  };

  const exportUserData = () => {
    const data = {
      user: {
        id: user.$id,
        name: user.name,
        email: user.email
      },
      profile: userProfile,
      shareLink: `https://id.adnimi.org/profile/${user.$id}`,
      exportedAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `adnimi-profile-${user.$id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  if (loading) {
    return (
      <div className="app-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (currentPage === 'login' && !user) {
    return (
      <div className="app-container">
        <div className="auth-card">
          <div className="logo-container">
            <img src="/adnimi.png" alt="Adnimi Logo" className="logo" />
          </div>
          
          <h1>Connexion</h1>
          <p className="subtitle">Accédez à votre compte Adnimi</p>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="form-container">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                onKeyPress={(e) => handleKeyPress(e, handleLogin)}
                placeholder="votre@email.com"
              />
            </div>

            <div className="form-group">
              <label>Mot de passe</label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                onKeyPress={(e) => handleKeyPress(e, handleLogin)}
                placeholder="••••••••"
              />
            </div>

            <button onClick={handleLogin} className="btn btn-primary">
              Se connecter
            </button>
          </div>

          <div className="divider">
            <span>ou</span>
          </div>

          <button className="btn btn-secondary" onClick={() => setCurrentPage('signup')}>
            Créer un compte
          </button>
        </div>
      </div>
    );
  }

  if (currentPage === 'signup' && !user) {
    return (
      <div className="app-container">
        <div className="auth-card">
          <div className="logo-container">
            <img src="/adnimi.png" alt="Adnimi Logo" className="logo" />
          </div>
          
          <h1>Créer un compte</h1>
          <p className="subtitle">Rejoignez Adnimi en quelques étapes</p>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="form-container">
            <div className="form-row">
              <div className="form-group">
                <label>Prénom</label>
                <input
                  type="text"
                  value={signupData.firstName}
                  onChange={(e) => setSignupData({...signupData, firstName: e.target.value})}
                  placeholder="Jean"
                />
              </div>

              <div className="form-group">
                <label>Nom</label>
                <input
                  type="text"
                  value={signupData.lastName}
                  onChange={(e) => setSignupData({...signupData, lastName: e.target.value})}
                  placeholder="Dupont"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={signupData.email}
                onChange={(e) => setSignupData({...signupData, email: e.target.value})}
                placeholder="votre@email.com"
              />
            </div>

            <div className="form-group">
              <label>Date de naissance</label>
              <input
                type="date"
                value={signupData.birthDate}
                onChange={(e) => setSignupData({...signupData, birthDate: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>Genre</label>
              <select
                value={signupData.gender}
                onChange={(e) => setSignupData({...signupData, gender: e.target.value})}
              >
                <option value="">Sélectionner</option>
                <option value="male">Homme</option>
                <option value="female">Femme</option>
                <option value="other">Autre</option>
                <option value="prefer-not-to-say">Préfère ne pas répondre</option>
              </select>
            </div>

            <div className="form-group">
              <label>Mot de passe</label>
              <input
                type="password"
                value={signupData.password}
                onChange={(e) => setSignupData({...signupData, password: e.target.value})}
                placeholder="Min. 8 caractères"
              />
            </div>

            <div className="form-group">
              <label>Confirmer le mot de passe</label>
              <input
                type="password"
                value={signupData.confirmPassword}
                onChange={(e) => setSignupData({...signupData, confirmPassword: e.target.value})}
                onKeyPress={(e) => handleKeyPress(e, handleSignup)}
                placeholder="••••••••"
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={signupData.acceptCGU}
                  onChange={(e) => setSignupData({...signupData, acceptCGU: e.target.checked})}
                />
                <span>
                  J'accepte les{' '}
                  <a href="https://cgu.adnimi.org" target="_blank" rel="noopener noreferrer">
                    Conditions Générales d'Utilisation
                  </a>
                </span>
              </label>
            </div>

            <button onClick={handleSignup} className="btn btn-primary">
              Créer mon compte
            </button>
          </div>

          <div className="divider">
            <span>ou</span>
          </div>

          <button className="btn btn-secondary" onClick={() => setCurrentPage('login')}>
            J'ai déjà un compte
          </button>
        </div>
      </div>
    );
  }

  if (currentPage === 'account' && user) {
    const shareLink = `https://id.adnimi.org/profile/${user.$id}`;

    return (
      <div className="app-container">
        <div className="account-container">
          <div className="account-header">
            <img src="/adnimi.png" alt="Adnimi Logo" className="logo-small" />
            <button className="btn-icon" onClick={handleLogout}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          <div className="profile-card">
            <div className="profile-avatar">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <h1>{user.name}</h1>
            <p className="profile-email">{user.email}</p>
            <div className="profile-id">ID: {user.$id}</div>
          </div>

          {userProfile && (
            <div className="info-card">
              <h2>Informations personnelles</h2>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Prénom</span>
                  <span className="info-value">{userProfile.firstName}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Nom</span>
                  <span className="info-value">{userProfile.lastName}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Date de naissance</span>
                  <span className="info-value">
                    {new Date(userProfile.birthDate).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Genre</span>
                  <span className="info-value">
                    {userProfile.gender === 'male' ? 'Homme' : 
                     userProfile.gender === 'female' ? 'Femme' : 
                     userProfile.gender === 'other' ? 'Autre' : 
                     'Préfère ne pas répondre'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="info-card">
            <h2>Lien de partage</h2>
            <div className="share-link-container">
              <input
                type="text"
                value={shareLink}
                readOnly
                className="share-link-input"
              />
              <button
                className="btn btn-copy"
                onClick={() => {
                  navigator.clipboard.writeText(shareLink);
                  setSuccess('Lien copié !');
                  setTimeout(() => setSuccess(''), 2000);
                }}
              >
                Copier
              </button>
            </div>
          </div>

          <div className="info-card">
            <h2>Exportation des données</h2>
            <p className="card-description">
              Téléchargez vos données au format JSON pour les intégrer dans d'autres applications
            </p>
            <button className="btn btn-primary" onClick={exportUserData}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{marginRight: '8px'}}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Exporter mes données
            </button>
          </div>

          {success && (
            <div className="floating-alert alert-success">
              {success}
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}

export default App;
